import {
  Component, OnInit, ViewChildren, QueryList, ViewChild, Input, Output,
  EventEmitter, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef, AfterViewInit
} from '@angular/core';
import { IFilterEventData, SidebarFilterComponent, SidebarFilterTypes } from '../sidebar-filter/sidebar-filter.component';
import { StarbaseNotificationTypes, StarbaseNotificationService, StarbaseNotificationSentiment } from 'starbase-notification';
import { ImageTypes, ImageType } from '../models/imageType';
import { environment } from 'src/environments/environment';
import {
  AlphaSortArrayByProperty, NumericSortArrayByProperty, onlyUniqueByKey, WriteOrdinalSuffixOfNumber, MONTHS, ISelectable, FilterParams
} from 'starbase-launcher';
import { LUArmy } from '../models/luArmy';
import { Army } from '../models/army';
import { Regiment } from '../models/regiment';
import { BranchOfService } from '../models/branchOfService';
import { Rank } from '../models/rank';
import { Weapon } from '../models/weapon';
import { BatchLog } from '../models/batchLog';
import { LUSource } from '../models/luSource';
import { CallNumberModel } from '../models/callNumberModel';
import { GalleryTypeService } from '../services/gallery-type.service';
import { LookupDataService } from '../services/lookup-data.service';
import { ImageDate } from '../models/imageDate';
import { State } from '../models/state';
import { RankCategory } from '../models/rankCategory';
import { PrimaryWeapon } from '../models/primaryWeapon';
import { SourceCallNoBoxComponent } from '../source-call-no-box/source-call-no-box.component';
import { UnitArmies } from '../models/unitLinking';
import { ImageFile } from '../models/imageFile';
import { Subscription, Observable } from 'rxjs';
import { ImageService } from '../services/image.service';
import { AppGlobalsService } from '../app-globals.service';
import { Battle } from '../models/battle';
import { ImageIndividual } from '../models/imageIndividual';
import { StarbaseModalButton, StarbaseModalOptions, StarbaseModalDialogService, StarbaseModalTypes } from 'starbase-modal-dialog';
import { StarbaseModalOverlayService } from 'modal-overlay';
import { LU_Keyword } from '../models/keyword';
import { KeywordDataService } from '../services/keyword-data.service';
import { BreakpointService, BreakpointState } from '../breakpoint.service';

class RadioFilter implements ISelectable {
  public val: string;
  public disp: string;
  public selected: boolean;

  constructor(val: string, disp: string) {
    this.selected = false;
    this.val = val;
    this.disp = disp;
  }
}

@Component({
  selector: 'app-library-filter',
  templateUrl: './library-filter.component.html',
  styleUrls: ['./library-filter.component.css'],
  changeDetection: ChangeDetectionStrategy.Default
})
export class LibraryFilterComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() isAdmin = false; // can also be true for Librarians
  @Input() enabled = true;
  @Input() imageTypeFilter: ImageType = new ImageType();
  @Input() civilianMode = false; // Civilian Research mode hides certain filters.
  @Input() rpp = 35; // default results per page for filterParams

  @Output() applyFilters: EventEmitter<FilterParams<ImageFile>> = new EventEmitter<FilterParams<ImageFile>>();
  @Output() applyCivilianResearchFilters: EventEmitter<FilterParams<ImageFile>> = new EventEmitter<FilterParams<ImageFile>>();
  @Output() errorEmitter: EventEmitter<string> = new EventEmitter<string>();
  @Output() resetEmitter = new EventEmitter<any>();
  
  @ViewChildren(SidebarFilterComponent) sidebarFiltersChildren !: QueryList<SidebarFilterComponent>;
  @ViewChild('sourceCallNoBox') sourceCallNoBox: SourceCallNoBoxComponent;

  public datesFilterData: ImageDate[] = [];
  public quarters: {
    quarter: number; selected: boolean;
  }[] = [
      { quarter: 1, selected: false },
      { quarter: 2, selected: false },
      { quarter: 3, selected: false },
      { quarter: 4, selected: false }
    ];
  public allYears = false;
  public allQtrs = false;
  public filterYearParams = [];
  public batchLogs: BatchLog[] = [];
  public imageTypeToBatch: any[] = [];
  public luSources: LUSource[] = [];
  public callNumberData: CallNumberModel[] = [];
  public filteredCallNumberData: CallNumberModel[] = [];
  public locationStates: State[] = [];
  public filterLocationParams = [];
  public allLocationStates = false;
  public allBattles = false;
  public allUnits = false;
  public battles: Battle[] = [];
  public locations: { location: string; selected: boolean }[] = [];
  public filterBattleParams = [];
  public allMounted = false;
  public mountedFilter: RadioFilter[] = [];
  public checkedOutFilter: RadioFilter[] = [];
  public fullyTaggedFilter: RadioFilter[] = [];
  public allArmies = false;
  public armies: LUArmy[] = [];
  public regionalArmies: Army[] = [];
  public filterArmyParams = [];
  public ranks: Rank[] = [];
  public rankCats: RankCategory[] = [];
  public allRanks = false;
  public regionalNames: { regionalName: string; selected: boolean }[] = [];
  public allRegionalNames = false;
  public regiments: Regiment[] = [];
  public branches: BranchOfService[] = [];
  public coLetters: string[] = [];
  public allBranches = false;
  public weapons: Weapon[] = [];
  public weaponCats: PrimaryWeapon[] = [];
  public allWeapons = false;
  public allBatches = false;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  public PFRadio: RadioFilter[] = [];
  public filterUnitParams = [];
  public filterTotals: number;
  public filtersAreReset = true;
  public filtersLoaded: boolean;
  public searchIndividual: ImageIndividual;
  sourceCallNoLevelOfSpecificity = 0;

  unitArmies = UnitArmies;

  months = MONTHS;
  shortMonths = [
    { val: 0, txt: ' ? ' },
    { val: 1, txt: 'Jan' },
    { val: 2, txt: 'Feb' },
    { val: 3, txt: 'Mar' },
    { val: 4, txt: 'Apr' },
    { val: 5, txt: 'May' },
    { val: 6, txt: 'Jun' },
    { val: 7, txt: 'Jul' },
    { val: 8, txt: 'Aug' },
    { val: 9, txt: 'Sep' },
    { val: 10, txt: 'Oct' },
    { val: 11, txt: 'Nov' },
    { val: 12, txt: 'Dec' }
  ];

  // filter expand toggles
  public filterExpandToggles =
    {
      lsopen: false,
      armyopen: false,
      battleopen: false,
      batchopen: false,
      yearsopen: false,
      quartersopen: false,
      pfopen: false,
      unitopen: false,
      mountedopen: false,
      branchopen: false,
      rankopen: false,
      weaponopen: false,
      regionalopen: false,
      checkedoutopen: false,
      fullytaggedopen: false,
      locationopen: false,
      sourcecallnoopen: false,
      individualopen: false
    };

  activeUnitFilters: any[] = [];
  showUnitPickerFilter: boolean;
  activeSourceCallNoFilters: CallNumberModel[] = [];
  activeSourceCallNoFilter: CallNumberModel;
  showSourceCallNoFilter: boolean;
  activeDateFilters: ImageDate[] = [];
  showDateFilter: boolean;
  showIndividualSearch: boolean;

  /* These Keep track of which filter parent is expanded to reveal inner filters. */
  armyExpansions: boolean[] = [];
  rankExpansions: boolean[] = [];
  weaponExpansions: boolean[] = [];
  batchExpansions: boolean[] = [];

  ordinalSuffix = WriteOrdinalSuffixOfNumber;
  sidebarFilterTypes = SidebarFilterTypes;

  // imageTypes = Object.keys(environment.typeOfImageIds).map(k => ({ val: environment.typeOfImageIds[k], txt: k, selected: false }));
  // imageTypes = Object.keys(ImageTypes).filter(a =>
  //   !(parseInt(a, 10) >= 0)).map(k => ({ val: environment.typeOfImageIds[k], txt: k, selected: false }));
  imageTypes = ImageType.imageTypes;

  isHandset: boolean = false;
  parentWidth$: Observable<number>;

  private _subscription;

  private imageFilterOptionsSubscription: Subscription;
  private coLettersSubscription: Subscription;
  private getQryOnce: Subscription;

  private subscriptionList: Subscription[] = [];


  constructor(private notiSvc: StarbaseNotificationService,
    public galleryTypeSvc: GalleryTypeService,
    private lookupSvc: LookupDataService,
    private imgSvc: ImageService,
    public appGlobalsSvc: AppGlobalsService,
    private modalSvc: StarbaseModalDialogService,
    private overlaySvc: StarbaseModalOverlayService,
    private keywordDataSvc: KeywordDataService,
    private breakpointService: BreakpointService,
    private cd: ChangeDetectorRef) { }

  ngOnInit() {
    this._subscription = this.breakpointService.handset$.subscribe(
      isHandset => {
        this.isHandset = isHandset;
      }
    );

    this.parentWidth$ = this.galleryTypeSvc.libraryFilterBarWidth$;
  }
  ngAfterViewInit(): void {
    this.getQryOnce = this.sidebarFiltersChildren.changes.subscribe((c: QueryList<SidebarFilterComponent>) => {
      this.cd.markForCheck();
      if (this.sumOfFiltersApplied() < 1) {
        c.forEach(cc => cc.resetFilter());
      }
      this.getQryOnce.unsubscribe();
    });
    this.subscriptionList.push(this.getQryOnce);
    if (this.sumOfFiltersApplied() < 1) {
      this.sidebarFiltersChildren.forEach(c => c.resetFilter());
    }
  }
  ngOnDestroy() {
    this.subscriptionList.forEach(s => {
      if (s) {
        s.unsubscribe();
      }
    });
  }

  sourceCallFilterPostRefresh(sourceCallIdsArray) {
    const sourceCallIds = sourceCallIdsArray[1].split('|');
    const sourceCallRecords = this.callNumberData.filter( x => sourceCallIds.some( y => parseInt(y, 10) === x.uid));
    const newCallNumber = new CallNumberModel();
    newCallNumber.uid = 1;
    newCallNumber.lU_Source = this.luSources.filter(x => x.uid === sourceCallRecords[0].lU_SourceUID)[0];

    let tempComp = sourceCallRecords[0].callNumberL1;
    if (sourceCallRecords.every(x => x.callNumberL1 === tempComp) && sourceCallIds[0].callNumberL1 !== '') {
      newCallNumber.callNumberL1 = tempComp;
      tempComp = sourceCallRecords[0].callNumberL2;

      if (sourceCallRecords.every(x => x.callNumberL2 === tempComp) && sourceCallIds[0].callNumberL2 !== '') {
        newCallNumber.callNumberL2 = tempComp;
        tempComp = sourceCallRecords[0].callNumberL3;

        if (sourceCallRecords.every(x => x.callNumberL3 === tempComp) && sourceCallIds[0].callNumberL3 !== '') {
          newCallNumber.callNumberL3 = tempComp;
          tempComp = sourceCallRecords[0].callNumberL4;

          if (sourceCallRecords.every(x => x.callNumberL4 === tempComp) && sourceCallIds[0].callNumberL4 !== '') {
            newCallNumber.callNumberL4 = tempComp;
          } else {
            newCallNumber.callNumberL4 = null;
          }
        } else {
          newCallNumber.callNumberL3 = null;
        }
      } else {
        newCallNumber.callNumberL2 = null;
      }
    } else {
      newCallNumber.callNumberL1 = null;
    }
    this.filteredCallNumberData = this.callNumberData.filter(c => c.lU_SourceUID === newCallNumber.lU_Source.uid && c.imagesExist);
    return newCallNumber;
  }

  updateFiltersPostRefresh() {
    const reloadedFilters = this.galleryTypeSvc.getCurrentLibraryFilters();
    this.activeUnitFilters = [];
    for (const filter of reloadedFilters.filters) {
      switch(filter[0]) {
        case 'TypeOfImageId':
          if (filter[1] && filter[1].length > 1){
          this.applyCivilianResearchFilters.emit(reloadedFilters);
            }
          break;

        case 'Portrait_Field':
          this.filterExpandToggles.pfopen = (this.filterExpandToggles.pfopen === true) ? true : false;
          if (filter[1] === 'portrait') {
            this.PFRadio[0].selected = false;
            this.PFRadio[1].selected = true;
          } else {
            this.PFRadio[0].selected = false;
            this.PFRadio[2].selected = true;
          }
          break;

        case 'Mounted':
          this.filterExpandToggles.mountedopen = (this.filterExpandToggles.mountedopen === true) ? true : false;
          if (filter[1] === 'true') {
            this.mountedFilter[0].selected = false;
            this.mountedFilter[1].selected = true;
          } else {
            this.mountedFilter[0].selected = false;
            this.mountedFilter[2].selected = true;
          }
          break;

        case 'CheckedOut':
          this.filterExpandToggles.checkedoutopen = (this.filterExpandToggles.checkedoutopen === true) ? true : false;
          if (filter[1] === 'true') {
            this.checkedOutFilter[0].selected = false;
            this.checkedOutFilter[1].selected = true;
          } else {
            this.checkedOutFilter[0].selected = false;
            this.checkedOutFilter[2].selected = true;
          }
          break;

        case 'FullyTagged':
          this.filterExpandToggles.checkedoutopen = (this.filterExpandToggles.fullytaggedopen === true) ? true : false;
          if (filter[1] === 'true') {
            this.fullyTaggedFilter[0].selected = false;
            this.fullyTaggedFilter[1].selected = true;
          } else {
            this.fullyTaggedFilter[0].selected = false;
            this.fullyTaggedFilter[2].selected = true;
          }
          break;

        case 'LocationStateId':
          this.filterExpandToggles.lsopen = (this.filterExpandToggles.lsopen === true) ? true : false;
          const selectedStateIDs = filter[1].split('|');
          this.locationStates.forEach(state => state.selected =
            selectedStateIDs.some(selectedStateID => parseInt(selectedStateID, 10) === state.uid));
          break;

        case 'Location':
          this.filterExpandToggles.locationopen = (this.filterExpandToggles.locationopen === true) ? true : false;
          const selectedLocations = filter[1].split('|');
          this.locations.forEach(loc => loc.selected = selectedLocations.some(selectedLoc => selectedLoc === loc.location));
          break;

        case 'BattleLinks.BattleUID':
          this.filterExpandToggles.battleopen = (this.filterExpandToggles.battleopen === true) ? true : false;
          const battleIDs = filter[1].split('|');
          this.battles.forEach(battle => battle.selected =
            battleIDs.some(selectedBattleID => parseInt(selectedBattleID, 10) === battle.uid));
          this.battles = this.battles.sort((a, b) => {
            if(a.selected && !b.selected){
              return -1
            } else if (!a.selected && b.selected){
              return 1
            }

            if(a.name < b.name){
              return -1
            } else if (a.name > a.name){
              return 1
            } else {
              return 0
            }
          })
          this.cd.detectChanges();
          break;

        case 'BatchNo':
          this.filterExpandToggles.batchopen = (this.filterExpandToggles.batchopen === true) ? true : false;
          const selectedBatchIDs = filter[1].split('|');
          this.batchLogs.forEach(batch => batch.selected =
            selectedBatchIDs.some(selectedBatchID => parseInt(selectedBatchID, 10) === batch.uid));
          break;

        case 'ImageIndividuals.RankId':
          this.filterExpandToggles.rankopen = (this.filterExpandToggles.rankopen === true) ? true : false;
          const selectedRankIDs = filter[1].split('|');
          this.ranks.forEach(rank => rank.selected =
            selectedRankIDs.some(selectedRankID => parseInt(selectedRankID, 10) === rank.uid));
          this.rankCats.filter(category => this.ranks.some(rank => rank.rankCategoryId === category.uid && rank.selected))
            .forEach(rankCat => rankCat.selected = this.ranks.filter(rank => rank.rankCategoryId === rankCat.uid)
              .every(rank => rank.selected === true));
          break;

        case 'ArmyId':
          this.filterExpandToggles.armyopen = (this.filterExpandToggles.armyopen === true) ? true : false;
          const selectedArmyIDs = filter[1].split('|');
          this.regionalArmies.forEach(army => army.selected =
            selectedArmyIDs.some(selectedArmyID => parseInt(selectedArmyID,10) === army.uid));
          this.armies.filter(region => this.regionalArmies.some(army => army.uid === region.uid && army.selected))
            .forEach(regArmy => regArmy.selected = this.regionalArmies.filter(army => army.uid === regArmy.uid)
              .every(army => army.selected === true));
          break;

        case 'Weapons':
          this.filterExpandToggles.weaponopen = (this.filterExpandToggles.weaponopen === true) ? true : false;
          const selectedWeaponIDs = filter[1].split('|');
          this.weapons.forEach(weapon => weapon.selected =
            selectedWeaponIDs.some(selectedWeaponID => parseInt(selectedWeaponID, 10) === weapon.uid));
          this.weaponCats.filter(category => this.weapons.some(weapon => weapon.primaryWeapon.uid === category.uid && weapon.selected))
            .forEach(weaponCat => weaponCat.selected = this.weapons.filter(weapon => weapon.primaryWeapon.uid === weaponCat.uid)
              .every(weapon => weapon.selected === true));
          break;

        case 'SourceCallNumberId':
          this.filterExpandToggles.sourcecallnoopen = (this.filterExpandToggles.sourcecallnoopen === true) ? true : false;
          const toUpdateParams = this.sourceCallFilterPostRefresh(filter);
          this.activeSourceCallNoFilter = toUpdateParams;
          this.activeSourceCallNoFilters.push(toUpdateParams);
          break;

        case 'CallNumberL1':
        case 'CallNumberL2':
        case 'CallNumberL3':
        case 'CallNumberL4':
          if (!this.activeSourceCallNoFilter) {
            this.activeSourceCallNoFilter = new CallNumberModel();
            this.activeSourceCallNoFilter.uid = 1;
            // eslint-disable-next-line max-len
            this.activeSourceCallNoFilter.callNumberL1 = this.activeSourceCallNoFilter.callNumberL2 = this.activeSourceCallNoFilter.callNumberL3 = this.activeSourceCallNoFilter.callNumberL4 = null;
          }
          this.activeSourceCallNoFilter[filter[0].charAt(0).toLowerCase() + filter[0].slice(1)] = filter[1];
          this.activeSourceCallNoFilters = this.callNumberData.filter(
            a => a.lU_SourceUID === this.activeSourceCallNoFilter.lU_SourceUID
              && a.callNumberL1 === this.activeSourceCallNoFilter.callNumberL1
              && (!this.activeSourceCallNoFilter.callNumberL2 || a.callNumberL2 === this.activeSourceCallNoFilter.callNumberL2)
              && (!this.activeSourceCallNoFilter.callNumberL3 || a.callNumberL3 === this.activeSourceCallNoFilter.callNumberL3)
              && (!this.activeSourceCallNoFilter.callNumberL4 || a.callNumberL4 === this.activeSourceCallNoFilter.callNumberL4)
          );
          break;

        case 'SourceId':
          if (!this.activeSourceCallNoFilter) {
            this.activeSourceCallNoFilter = new CallNumberModel();
            this.activeSourceCallNoFilter.uid = 1;
            // eslint-disable-next-line max-len
            this.activeSourceCallNoFilter.callNumberL1 = this.activeSourceCallNoFilter.callNumberL2 = this.activeSourceCallNoFilter.callNumberL3 = this.activeSourceCallNoFilter.callNumberL4 = null;
          }
          this.activeSourceCallNoFilter.lU_SourceUID = +filter[1];
          this.activeSourceCallNoFilter.lU_Source = this.luSources.filter(x => x.uid === this.activeSourceCallNoFilter.lU_SourceUID)[0];
          // eslint-disable-next-line max-len
          this.filteredCallNumberData = this.callNumberData.filter(c => c.lU_SourceUID === this.activeSourceCallNoFilter.lU_Source.uid && c.imagesExist);
          this.activeSourceCallNoFilters = this.callNumberData.filter(
            a => a.lU_SourceUID === this.activeSourceCallNoFilter.lU_SourceUID
              && a.callNumberL1 === this.activeSourceCallNoFilter.callNumberL1
              && (!this.activeSourceCallNoFilter.callNumberL2 || a.callNumberL2 === this.activeSourceCallNoFilter.callNumberL2)
              && (!this.activeSourceCallNoFilter.callNumberL3 || a.callNumberL3 === this.activeSourceCallNoFilter.callNumberL3)
              && (!this.activeSourceCallNoFilter.callNumberL4 || a.callNumberL4 === this.activeSourceCallNoFilter.callNumberL4)
          );
          break;

         case 'RelatedImageSet':
          this.galleryTypeSvc.storeRelatedSetNameFilter(filter[1]); 
          break;

         case 'ImageKeywords.LU_KeywordUID':
          let currentKeywordUID = filter[1];
          this.keywordDataSvc.getOneKeyword(currentKeywordUID).subscribe(kw => this.galleryTypeSvc.storeKeywordFilter(kw.keyword));
          break;
      }
    }
    if (reloadedFilters.orClusterFilters && reloadedFilters.orClusterFilters.length > 0) {
      this.showDateFilter = (this.showDateFilter === true) ? true : false;
      this.showUnitPickerFilter = (this.showUnitPickerFilter === true) ? true : false;
      let dateFlag = false;
      let unitFlag = false;
      for (const iterable of reloadedFilters.orClusterFilters) {
        const theDate = new ImageDate(undefined);
        theDate.year = null;
        theDate.month = null;
        theDate.day = null;
        theDate.quarter = null;
        const theUnit = {
          army: null,
          corps: null,
          regiment: null,
          state: null,
          branch: null,
          coLetters: null
        };

        for (let i = 0 ; i < iterable.length ; i += 2){
          switch(iterable[i]) {
            case 'RelevantDates.Year':
              const year = iterable[i+1];
              theDate.year = +year;
              dateFlag = true;
              break;

            case 'RelevantDates.Month':
              const month = iterable[i+1];
              theDate.month = +month;
              theDate.longMonthString = this.months.find(x => x.val === theDate.month).txt;
              dateFlag = true;
              break;

            case 'RelevantDates.Day':
              const day = iterable[i+1];
              theDate.day = +day;
              dateFlag = true;
              break;

            case 'RelevantDates.Quarter':
              const quarter = iterable[i+1];
              theDate.quarter = +quarter;
              dateFlag = true;
              break;

            case 'Units.Army':
              const army = iterable[i+1];
              theUnit.army = army;
              unitFlag = true;
              break;

            case 'Units.Corps':
              const corps = iterable[i+1];
              theUnit.corps = corps;
              unitFlag = true;
              break;

            case 'Units.RegimentNoId':
              const regimentNoId = iterable[i+1];
              theUnit.regiment = this.regiments.filter(regiment => regiment.uid === parseInt(regimentNoId, 10))[0];
              unitFlag = true;
              break;

            case 'Units.StateId':
              const stateId = iterable[i+1];
              theUnit.state = this.locationStates.filter(x => x.uid === parseInt(stateId, 10))[0];
              unitFlag = true;
              break;

            case 'Units.BranchId':
              const branchId = iterable[i+1];
              theUnit.branch = this.branches.filter(branch => branch.uid === parseInt(branchId, 10))[0];
              unitFlag = true;
              break;

            case 'Units.CompanyLetters':
              const companyLetters = iterable[i+1].split('|');
              const individualElements = companyLetters.filter(ele => ele.length === 1);
              theUnit.coLetters = [individualElements];
              unitFlag = true;
              break;
          }
        }

        if (unitFlag === true) {
          // Check for duplicates using JSON comparison
          const isDuplicate = this.activeUnitFilters.some(existing => 
            JSON.stringify(existing) === JSON.stringify(theUnit)
          );
          if (!isDuplicate) {
            this.activeUnitFilters.push(theUnit);
          }
          unitFlag = false;
        }
        if (dateFlag === true) {
          const isDuplicate = this.activeDateFilters.some(existing => 
            JSON.stringify(existing) === JSON.stringify(theDate)
          );
          if (!isDuplicate) {
            this.activeDateFilters.push(theDate);
          }
          dateFlag = false;
        }
      }
    }
    if (reloadedFilters.searchFilters && reloadedFilters.searchFilters.length > 0) {
      this.searchIndividual = new ImageIndividual();
      this.searchIndividual.rank = new Rank();

      for (const filter of reloadedFilters.searchFilters) {
        switch (filter[0]) {
          case 'FirstName':
            this.searchIndividual.firstName = filter[1];
            break;
          case 'LastName':
            this.searchIndividual.lastName = filter[1];
        }
      }

      if (this.ranks.filter(r => r.selected).length === 1) {
        this.searchIndividual.rank = this.ranks.filter(r => r.selected)[0];
        this.searchIndividual.rankId = this.searchIndividual.rank.uid;
      } else if (this.rankCats.filter(rc => rc.selected).length === 1) {
        this.searchIndividual.rank.rankCategory = this.rankCats.filter(rc => rc.selected)[0];
        this.searchIndividual.rank.rankCategoryId = this.searchIndividual.rank.rankCategory.uid;
      }

      if (this.activeUnitFilters.length === 1) {
        const activeUnit = this.activeUnitFilters[0];
        this.searchIndividual.army = parseInt(activeUnit.army, 10);
        this.searchIndividual.branch = activeUnit.branch;
        this.searchIndividual.companyLetters = activeUnit.coLetters.join(',');
        this.searchIndividual.corps = parseInt(activeUnit.corps, 10);
        this.searchIndividual.regimentNo = activeUnit.regiment;
        this.searchIndividual.state = activeUnit.state;
        this.searchIndividual.stateId = this.searchIndividual.state?.uid;
      }
    }
  }

  public sumOfFiltersApplied(): number {
    return this.galleryTypeSvc.getRelatedSetNameFilter?.length > 0 ? this.galleryTypeSvc.CountOfFiltersIgnoreImageType + 1 : this.galleryTypeSvc.CountOfFiltersIgnoreImageType;
  }

  public resetFilters(apply: boolean, cf?: FilterParams<ImageFile>, emitReset = true, clearSearchTerm = true): FilterParams<ImageFile> {
    if (!this.enabled) {
      this.notiSvc.postNotification(
        this.notiSvc.notificationBuilder(
          'Reset is not available',
          'Filters cannot be changed when in Selection Mode. Cancel Selection Mode first, then try again.',
          undefined, undefined, StarbaseNotificationSentiment.Notice));
      return;
    }
    let fp: FilterParams<ImageFile>;
    if (cf) {
      fp = cf;
      if (clearSearchTerm) {
        fp.resetFilters(); // then we can reset everything
      } else { // else, don't clear the search phrase
        fp.clearFilterArray();
        fp.clearClusterFilter();
      }
    } else {
      if (clearSearchTerm === false) {
        console.warn('Flag to preserve search was set but no existing FilterParams was received.');
      }
      fp = new FilterParams<ImageFile>();
    }
    
    fp.filters = fp.filters || [];
    fp.orClusterFilters = fp.orClusterFilters || [];
    fp.searchFilters = fp.searchFilters || [];
    fp.sortFields = fp.sortFields || [];
    fp.explicitSearchProperties = fp.explicitSearchProperties || [];
    
    fp.rpp = this.rpp;
    if (this.imageTypeFilter.uid !== 0) {
      fp.filters.push(['TypeOfImageId', this.imageTypeFilter.uid]);
    } else if (this.galleryTypeSvc.prePrintedMode) {
      this.galleryTypeSvc.PrePrintedTypes.subscribe(ppt => ppt.forEach(p => {
        if (p.selected) {
          fp.addToFilter('TypeOfImageId', p.uid);
        }
      }));
    }
    this.galleryTypeSvc.setCurrentLibraryFilters(fp);
    this.galleryTypeSvc.storeKeywordFilter('');

    this.filtersAreReset = true;
    try {
      this.sidebarFiltersChildren.forEach(s => s.resetFilter());
    } catch { }
    this.armies.forEach(a => a.selected = false);
    this.regionalArmies.forEach(a => a.selected = false);
    this.branches.forEach(b => b.selected = false);
    this.rankCats.forEach(a => a.selected = false);
    this.ranks.forEach(a => a.selected = false);
    this.weapons.forEach(a => a.selected = false);
    this.weaponCats.forEach(a => a.selected = false);
    this.battles.forEach(a => a.selected = false);
    this.batchLogs.forEach(a => a.selected = false);
    ImageType.imageTypes.forEach(t => t.selected = false);

    this.galleryTypeSvc.clearCurrentFilterOptions();

    this.allArmies = false;
    this.allRanks = false;
    this.allUnits = false;
    this.allBranches = false;
    this.allWeapons = false;
    this.allBatches = false;
    this.allBattles = false;
    try {
      this.checkedOutFilter[0].selected = true;
    } catch { }
    try {
      this.fullyTaggedFilter[0].selected = true;
    } catch { }
    this.activeUnitFilters = [];
    this.activeDateFilters = [];
    this.activeSourceCallNoFilter = new CallNumberModel();
    this.activeSourceCallNoFilter.uid = 1;
    // eslint-disable-next-line max-len
    this.activeSourceCallNoFilter.callNumberL1 = this.activeSourceCallNoFilter.callNumberL2 = this.activeSourceCallNoFilter.callNumberL3 = this.activeSourceCallNoFilter.callNumberL4 = null;
    this.activeSourceCallNoFilters = [];
    if (this.sourceCallNoBox) {
      this.sourceCallNoBox.closeFilterBox(true);
    }
    this.searchIndividual = undefined;

    this.closeFilterBoxes();

    console.log('Filters reset.');
    this.cd.detectChanges();
    // eslint-disable-next-line curly
    if (apply) this.applyFilters.emit(fp);
    if (emitReset) {
      this.resetEmitter.emit();
    } // inform other components we have reset.
    return fp;
  }

  clearAllFilterDisplays() {
    try {
      this.sidebarFiltersChildren.forEach(s => s.resetFilter());
    } catch { }
    this.armies.forEach(a => a.selected = false);
    this.regionalArmies.forEach(a => a.selected = false);
    this.branches.forEach(b => b.selected = false);
    this.rankCats.forEach(a => a.selected = false);
    this.ranks.forEach(a => a.selected = false);
    this.weapons.forEach(a => a.selected = false);
    this.weaponCats.forEach(a => a.selected = false);
    this.battles.forEach(a => a.selected = false);
    this.batchLogs.forEach(a => a.selected = false);
    ImageType.imageTypes.forEach(t => t.selected = false);

    this.allArmies = false;
    this.allRanks = false;
    this.allUnits = false;
    this.allBranches = false;
    this.allWeapons = false;
    this.allBatches = false;
    this.allBattles = false;
    try {
      this.checkedOutFilter[0].selected = true;
    } catch { }
    try {
      this.fullyTaggedFilter[0].selected = true;
    } catch { }
    this.activeUnitFilters = [];
    this.activeDateFilters = [];
    this.activeSourceCallNoFilter = new CallNumberModel();
    this.activeSourceCallNoFilter.uid = 1;
    // eslint-disable-next-line max-len
    this.activeSourceCallNoFilter.callNumberL1 = this.activeSourceCallNoFilter.callNumberL2 = this.activeSourceCallNoFilter.callNumberL3 = this.activeSourceCallNoFilter.callNumberL4 = null;
    this.activeSourceCallNoFilters = [];
    if (this.sourceCallNoBox) {
      this.sourceCallNoBox.closeFilterBox(true);
    }
    this.searchIndividual = undefined;

    this.closeFilterBoxes();

    console.log('Filters reset.');
    this.cd.detectChanges();
  }

  collapseAllFilters(callingFilter: string): void {
    const wasOpen = this.filterExpandToggles[callingFilter];
    this.closeFilterBoxes();
    Object.keys(this.filterExpandToggles).forEach(element => {
      this.filterExpandToggles[element] = false;
    });
    this.sidebarFiltersChildren.forEach(s => s.collapse());
    if (!wasOpen) {
      if (callingFilter === 'locationopen') {
        const modal = new StarbaseModalOptions(
          'Warning',
          // eslint-disable-next-line max-len
          'Location Filter is in a BETA status and refinements to improve this option are currently being performed.',
          [
            new StarbaseModalButton('Okay', () => {
              this.modalSvc.closeModal('User dismissed warning');
            }),
          ], StarbaseModalTypes.Warning);
        modal.dismissable = true;
        modal.forcedWidth = this.isHandset ? '90vw' : '';
        this.modalSvc.postModal(modal);
      }
      this.filterExpandToggles[callingFilter] = true;
    }
    this.cd.detectChanges();
  }
  expandAllFilters(): void {
    Object.keys(this.filterExpandToggles).forEach(element => {
      this.filterExpandToggles[element] = true;
    });
    this.cd.detectChanges();
  }

  getSelectedCount(arr: ISelectable[]): number {
    return arr.filter(a => a.selected).length;
  }

  public loadFilterOptionsData() {
    this.filtersLoaded = false;
    // this.clearFilterData(); // reset it all to prevent duplications during Batch Edit activity.
    this.imageFilterOptionsSubscription = this.imgSvc.getImageFilterOptions().subscribe(o => {
      this.coLettersSubscription = this.lookupSvc.getAllCompanyLetters().subscribe(l => this.coLetters = l); // static data
      this.subscriptionList.push(this.coLettersSubscription);
      this.locations = this.galleryTypeSvc.getCurrentFilterOptions('Locations');
      this.weapons = this.galleryTypeSvc.getCurrentFilterOptions('Weapons');
      this.regionalArmies = this.galleryTypeSvc.getCurrentFilterOptions('RegionalArmy');
      this.battles = this.galleryTypeSvc.getCurrentFilterOptions('Battles');
      this.ranks = this.galleryTypeSvc.getCurrentFilterOptions('Ranks');
      this.regionalNames = this.galleryTypeSvc.getCurrentFilterOptions('Regions');
      this.locationStates = this.galleryTypeSvc.getCurrentFilterOptions('Location States');
      this.batchLogs = this.galleryTypeSvc.getCurrentFilterOptions('Upload Batches');
      this.quarters = this.galleryTypeSvc.getCurrentFilterOptions('Quarters');
      this.mountedFilter = this.galleryTypeSvc.getCurrentFilterOptions('Mounted');
      this.checkedOutFilter = this.galleryTypeSvc.getCurrentFilterOptions('Checked Out');
      this.fullyTaggedFilter = this.galleryTypeSvc.getCurrentFilterOptions('Fully Tagged');
      this.PFRadio = this.galleryTypeSvc.getCurrentFilterOptions('Portrait/Field');
      this.activeDateFilters = this.galleryTypeSvc.getCurrentFilterOptions('RelevantDates');
      this.activeUnitFilters = this.galleryTypeSvc.getCurrentFilterOptions('Units');
      this.activeSourceCallNoFilters = this.galleryTypeSvc.getCurrentFilterOptions('SourceCallNo');

      if (!this.mountedFilter.length) {
        this.mountedFilter = [
          new RadioFilter('All', 'All'),
          new RadioFilter('true', 'Mounted'),
          new RadioFilter('false', 'Not Mounted')
        ];
        this.mountedFilter[0].selected = true;
      }
      if (!this.checkedOutFilter.length) {
        this.checkedOutFilter = [
          new RadioFilter('All', 'All'),
          new RadioFilter('true', 'Checked Out Only'),
          new RadioFilter('false', 'Checked In Only')
        ];
        this.checkedOutFilter[0].selected = true;
      }
      if (!this.fullyTaggedFilter.length) {
        this.fullyTaggedFilter = [
          new RadioFilter('All','All'),
          new RadioFilter('true', 'Fully Tagged'),
          new RadioFilter('false', 'Not Fully Tagged')
        ];
        this.fullyTaggedFilter[0].selected = true;
      }
      if (!this.PFRadio.length) {
        this.PFRadio = [
          new RadioFilter('All', 'All'),
          new RadioFilter('portrait', 'Portrait'),
          new RadioFilter('field', 'Field')
        ];
        this.PFRadio[0].selected = true;
      }

      if (!this.locationStates.length) {
        this.locationStates = o['locationStates']; this.locationStates = AlphaSortArrayByProperty(this.locationStates, 'abbreviation');
      }
      if (!this.battles.length) {
        this.battles = o['battles'];
        this.battles = AlphaSortArrayByProperty(this.battles, 'name');
      }
      this.armies = o['armies']; LUArmy.setupArmyCollection(this.armies); this.armyExpansions = new Array(this.armies.length);
      if (!this.regionalArmies.length) {
        this.regionalArmies = o['regionalArmies'];
        Army.setupArmyCollection(this.regionalArmies);
        this.regionalArmies = AlphaSortArrayByProperty(this.regionalArmies, 'army.armyName');
      }
      this.regiments = o['regiments']; this.regiments = NumericSortArrayByProperty(this.regiments, 'regimentNo');
      Regiment.setupCollection(this.regiments);
      o['dates'].forEach(d => {
        d['selected'] = false; this.datesFilterData.push(d);
      });
      this.branches = o['branches']; this.branches.forEach(b => new BranchOfService().deserialize(b));

      this.ranks = o['ranks']; Rank.setupRankCollection(this.ranks); this.ranks = AlphaSortArrayByProperty(this.ranks, 'rankCategoryName');
      this.ranks.forEach(r => this.rankCats.push(r.rankCategory)); this.rankCats = onlyUniqueByKey(this.rankCats, 'uid');
      this.rankExpansions = new Array(this.rankCats.length);

      if (!this.regionalNames.length) {
        this.regionalNames = o['regionalNames'].map(n => ({ regionalName: n, selected: false }));
      }
      if (!this.locations.length) {
        this.locations = o['locations'].map((n: string) => ({ location: n, selected: false }));
        this.locations = Object.assign([], this.locations.sort((a, b) => {
          if(a.location.toLocaleLowerCase() < b.location.toLocaleLowerCase()){
              return -1;
          } else if (a.location.toLocaleLowerCase() > b.location.toLocaleLowerCase()){
              return 1;
          } else {
              return 0;
          }
        }));
      }
      
      if (!this.weapons.length) {
        (this.weapons as Weapon[]) = o['weapons'];
      }
      this.weapons = Weapon.setupWeaponCollection(this.weapons);
      this.weapons = AlphaSortArrayByProperty(this.weapons, 'weaponDisplay');
      this.weapons.forEach(w => this.weaponCats.push(w.primaryWeapon)); this.weaponCats = onlyUniqueByKey(this.weaponCats, 'uid');
      this.weaponExpansions = new Array(this.weaponCats.length);

      if (!this.batchLogs.length && o['batchNos']) {
        const batchLogs = o['batchNos']; this.batchLogs = [];
        for (const b of batchLogs) {
          this.batchLogs.push(new BatchLog(b.numberOfFilesSelected, b.imageTypeId, b.batchName).deserialize(b));
        }
        this.batchLogs = NumericSortArrayByProperty(this.batchLogs, 'uid', false);
      }

      if (this.imageTypeFilter.uid !== 0) {
        // only show batch data for the current filtered types, unless in 'All' mode
        this.batchLogs = this.batchLogs.filter(l => l.imageTypeId === this.imageTypeFilter.uid);
        this.batchLogs = Object.assign([], this.batchLogs.sort((a, b) => {
          if(a.batchName.toLocaleLowerCase() < b.batchName.toLocaleLowerCase()){
              return -1;
          } else if (a.batchName.toLocaleLowerCase() > b.batchName.toLocaleLowerCase()){
              return 1;
          } else {
              return 0;
          }
        }));
      }
      this.batchExpansions = new Array(ImageType.imageTypes.length); // read count of image types in Environment file.

      this.luSources = o['sources'];
      this.luSources.forEach(s => new LUSource().deserialize(s));
      this.luSources = onlyUniqueByKey(this.luSources, 'source');
      this.luSources = AlphaSortArrayByProperty(this.luSources, 'source');
      this.callNumberData = o['callNumbers'];
      this.callNumberData.forEach(c => new CallNumberModel().deserialize(c));

      if (this.activeSourceCallNoFilters?.length > 0) {
        this.activeSourceCallNoFilter = this.activeSourceCallNoFilters[0];
        // eslint-disable-next-line max-len
        this.filteredCallNumberData = this.callNumberData.filter(c => c.lU_SourceUID === this.activeSourceCallNoFilter.lU_Source.uid && c.imagesExist);
      }

      this.updateFiltersPostRefresh();
    },
    err => {
        const e = typeof (err) === 'string' ? err : err['friendly'];
      this.notiSvc.postNotification(
        this.notiSvc.notificationBuilder(
          'Could not load filter option data',
          e,
          undefined, undefined, StarbaseNotificationSentiment.Error));
        this.errorEmitter.emit(e);
        console.error(err);
    },
    () => {
      this.filtersLoaded = true;
      if (this.sumOfFiltersApplied() < 1) {
        // this.resetFilters(false, null, false, true);
        try {
          this.sidebarFiltersChildren.forEach(c => c.resetFilter());
        } catch { }
      }
      this.cd.detectChanges();
    });
    this.subscriptionList.push(this.imageFilterOptionsSubscription);
  }

  // Used to indicate SourceCallNo level of Call number dropdowns chosen in the filter box.
  changeLevelOfSpec(n: number) {
    this.sourceCallNoLevelOfSpecificity = n;
    this.cd.detectChanges();
  }

  openUnitPicker(): void {
    this.closeFilterBoxes();
    this.showUnitPickerFilter = true;
    this.cd.detectChanges();
  }
  openIndividualSearch() {
    this.closeFilterBoxes();
    this.showIndividualSearch = true;
    this.cd.detectChanges();
  }
  openDateFilter(): void {
    this.closeFilterBoxes();
    this.showDateFilter = true;
    this.cd.detectChanges();
  }
  openSourceCallNoFilter(): void {
    this.closeFilterBoxes();
    this.showSourceCallNoFilter = true;
    this.cd.detectChanges();
  }
  closeFilterBoxes(): void {
    this.showDateFilter = false;
    this.showSourceCallNoFilter = false;
    this.showUnitPickerFilter = false;
    this.showIndividualSearch = false;
    this.cd.detectChanges();
  }


  filterCallNumbersBySource(s: LUSource): void {
    if (s && s.uid > 0) {
      this.filteredCallNumberData = this.callNumberData.filter(c => c.lU_SourceUID === s.uid && c.imagesExist);
      if (!this.activeSourceCallNoFilter) {
        this.activeSourceCallNoFilter = new CallNumberModel();
        this.activeSourceCallNoFilter.uid = 1;
      } else {
        this.activeSourceCallNoFilter.callNumberL1 = null;
        this.activeSourceCallNoFilter.callNumberL2 = null;
        this.activeSourceCallNoFilter.callNumberL3 = null;
        this.activeSourceCallNoFilter.callNumberL4 = null;
      }
      this.activeSourceCallNoFilter.lU_Source = s;
      this.activeSourceCallNoFilter.lU_SourceUID = s.uid;
      this.sourceCallNoBox.resetInnerCmpDropdowns();
      this.setCallNoFilter({callNumber: '', level: 1});
    } else {
      this.activeSourceCallNoFilter = new CallNumberModel();
      this.activeSourceCallNoFilter.uid = 1;
      this.activeSourceCallNoFilters = [];
      this.filteredCallNumberData = [];
      this.setCallNoFilter({callNumber: '', level: 1});
      this.cd.detectChanges();
    }
  }
  translateDateToFilter(): void {
    const df = {
      selected: true,
      entity: 'RelevantDates',
      obj: (this.activeDateFilters.length > 0) ? this.activeDateFilters : 'All',
      allSelected: null
    };
    this.updateFilter(df);
  }
  translateUnitToFilter(): void {
    const uf = {
      selected: true,
      entity: 'Units',
      obj: (this.activeUnitFilters.length > 0) ? this.activeUnitFilters : 'All',
      allSelected: null
    };
    this.updateFilter(uf);
  }
  setSourceCallNoFilter(e: CallNumberModel[]): void {
    this.activeSourceCallNoFilters = e;
    if (!this.activeSourceCallNoFilter) {
      this.activeSourceCallNoFilter = new CallNumberModel();
      this.activeSourceCallNoFilter.uid = 1;
    }
    this.activeSourceCallNoFilter.lU_Source = e[0].lU_Source;
    this.activeSourceCallNoFilter.lU_SourceUID = e[0].lU_SourceUID;
    const sf = {
      selected: true,
      entity: 'SourceCallNo',
      obj: (e && e.length) ? e : null,
      allSelected: null
    };
    this.updateFilter(sf);
  }
  setCallNoFilter(e: { callNumber: string; level: number }): void {
    const sf = {
      selected: true,
      entity: 'SourceCallNo',
      obj: e,
      allSelected: null
    };
    this.updateFilter(sf);
  }

  updateFilter(data: IFilterEventData): void {
    if (!this.enabled) {
      this.notiSvc.postNotification(
        this.notiSvc.notificationBuilder(
          'Filters are unavailable',
          'Filters can not be changed when in Selection Mode. Cancel Selection Mode first, then try again.',
          undefined, 4000, StarbaseNotificationSentiment.Notice));
      return;
    }
    this.filtersAreReset = false;
    let cf = this.galleryTypeSvc.getCurrentLibraryFilters(this.rpp);
    if (!cf) {
      console.error('Got invalid filter params data.');
    }
    cf.page = 1; // filters changed means result set will change, so move back to first page of results
    switch (data.entity) {
      case 'Army':
        if (data.obj === 'All') {
          this.armies.forEach(army => army.selected = data.selected);
          this.regionalArmies.forEach(r => r.selected = data.selected);
        } else {
          for (const r of this.regionalArmies) {
            if (r.armyId === data.obj.uid) {
              r.selected = data.selected;
            }
          }
        }
        this.updateFilter({
          selected: data.selected,
          entity: 'RegionalArmy',
          obj: '',
          allSelected: data.allSelected
        }); // then run again to apply the newly-selected or newly-unselected regional armies
        return;
        //break;
      case 'RelevantDates':
        if (data.obj === 'All') {
          this.activeDateFilters = [];
        }
        // cf.clearClusterFilter();
        for (let o = cf.orClusterFilters.length - 1; o >= 0; o--) {
          if (cf.orClusterFilters[o][0].includes('RelevantDates')) {
            cf.orClusterFilters.splice(o, 1);
          }
        }

        for (const df of data.obj) {
          if (!df || typeof (df) === 'string') {
            continue;
          }
          const tempCluster = [];
          if (df.year !== null) {
            tempCluster.push('RelevantDates.Year', df.year);
          }
          if (df.month !== null) {
            tempCluster.push('RelevantDates.Month', df.month);
          }
          if (df.day !== null) {
            tempCluster.push('RelevantDates.Day', df.day);
          }
          if (df.quarter !== null) {
            tempCluster.push('RelevantDates.Quarter', df.quarter);
          }
          cf.addANDClusterFilter(tempCluster);
          this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.activeDateFilters);
        }
        break;
      case 'Units':
        if (data.obj === 'All') {
          this.activeUnitFilters = [];
        }
        // cf.clearClusterFilter();
        for (let o = cf.orClusterFilters.length - 1; o >= 0; o--) {
          if (cf.orClusterFilters[o][0].includes('Units')) {
            cf.orClusterFilters.splice(o, 1);
          }
        }

        // Only process if we have valid unit filters
        if (data.obj !== 'All' && Array.isArray(data.obj)) {
          for (const uf of data.obj) {
            if (typeof (uf) === 'undefined' || typeof (uf) === 'string') {
              continue;
            }
            const tempCluster = [];
            if (uf.army !== null && uf.army !== 0) {
              tempCluster.push('Units.Army', uf.army);
            }
            if (uf.corps !== null && uf.corps !== 0) {
              tempCluster.push('Units.Corps', uf.corps);
            }
            if (uf.regiment !== null && uf.regiment !== '*') {
              tempCluster.push('Units.RegimentNoId', uf.regiment.uid);
            }
            if (uf.state !== null && uf.state !== '*') {
              tempCluster.push('Units.StateId', uf.state.uid);
            }
            if (uf.branch !== null && uf.branch !== '*') {
              tempCluster.push('Units.BranchId', uf.branch.uid);
            }
            if (uf.coLetters && uf.coLetters.length > 0) {
              let pipeBuilder = '';
              // more than one letter used for this unit.
              // This means that beyond adding all of the letters to the OR filter individually,
              // they also need to be added to the OR filter as comma delimited,
              // so we don't miss out on unit entries that belong to both of the company letters.
              if (uf.coLetters.length > 1) {
                pipeBuilder += uf.coLetters.join() + '|';
              }
              for (const c of uf.coLetters) {
                if (c === '*' || c === '') {
                  continue;
                } // ignore star calls
                pipeBuilder += c.trim() + '|';
              }
              pipeBuilder = pipeBuilder.slice(0, -1); // get rid of the final `|`.
              if (pipeBuilder.length > 0) {
                tempCluster.push('Units.CompanyLetters', pipeBuilder);
              }
            }
            if (tempCluster.length > 0) {
              cf.addANDClusterFilter(tempCluster);
            }
          }
        }

        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.activeUnitFilters);
        break;
      case 'SourceCallNo':
        // check if complete CallNumber model. If not, then we are using wildcards. Match 'All' in Call# Levels past 1...
        if (Array.isArray(data.obj)) { // wildcard signal
          // TODO: Log to Console
          console.log(data.obj);
        } else if (data.obj) {
          cf.replaceFilter('CallNumberL' + data.obj.level, data.obj.callNumber);
          cf.replaceFilter('SourceId', this.activeSourceCallNoFilter.lU_SourceUID ? this.activeSourceCallNoFilter.lU_SourceUID : '');
          this.activeSourceCallNoFilter['callNumberL' + data.obj.level] = data.obj.callNumber;
        } else {
          cf.replaceFilter('CallNumberL1', '');
          cf.replaceFilter('CallNumberL2', '');
          cf.replaceFilter('CallNumberL3', '');
          cf.replaceFilter('CallNumberL4', '');
        }
        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.activeSourceCallNoFilters);
        break;
      case 'RegionalArmy':
        cf.replaceFilter('ArmyId', '');
        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.regionalArmies);
        SidebarFilterComponent.pollSelectedOnFilterObjects(this.regionalArmies, 'uid').subscribe(s => cf.addToFilter('ArmyId', s));
        this.armies = Object.assign([], this.armies.sort((a, b) => {
            if(a.selected && !b.selected){
              return -1;
            } else if (!a.selected && b.selected){
              return 1;
            }

            if(a.armyName.toLocaleLowerCase() < b.armyName.toLocaleLowerCase()){
              return -1;
            } else if (a.armyName.toLocaleLowerCase() > b.armyName.toLocaleLowerCase()){
              return 1;
            } else {
              return 0;
            }
        }));
        break;
      case 'Battles':
        if (data.obj === 'All') {
            this.battles.forEach(b => b.selected = !!data.selected);
        }
        cf.replaceFilter('BattleLinks.BattleUID', '');
        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.battles);
        SidebarFilterComponent.pollSelectedOnFilterObjects(this.battles, 'uid').subscribe(s => cf.addToFilter('BattleLinks.BattleUID', s));
        this.battles = Object.assign([], this.battles.sort((a, b) => {
            if(a.selected && !b.selected){
              return -1;
            } else if (!a.selected && b.selected){
              return 1;
            }

            if(a.name.toLocaleLowerCase() < b.name.toLocaleLowerCase()){
              return -1;
            } else if (a.name.toLocaleLowerCase() > b.name.toLocaleLowerCase()){
              return 1;
            } else {
              return 0;
            }
        }));
        break;
      case 'Locations':
        if (data.obj === 'All') {
          this.locations.forEach(l => l.selected = !!data.selected);
        }
        cf.replaceFilter('Location', '');
        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.locations);
        data.allSelected.subscribe(s => cf.addToFilter('Location', s));
        this.locations = Object.assign([], this.locations.sort((a, b) => {
            if(a.selected && !b.selected){
              return -1;
            } else if (!a.selected && b.selected){
              return 1;
            }

            if(a.location.toLocaleLowerCase() < b.location.toLocaleLowerCase()){
              return -1;
            } else if (a.location.toLocaleLowerCase() > b.location.toLocaleLowerCase()){
              return 1;
            } else {
              return 0;
            }
        }));
        break;
      case 'RankCats':
        if (data.obj === 'All') {
          this.rankCats.forEach(rc => rc.selected = data.selected);
          this.ranks.forEach(r => r.selected = data.selected);
        } else {
          for (const r of this.ranks) {
            if (r.rankCategoryId === data.obj.uid) {
              r.selected = data.selected;
            }
          }
        }
        this.updateFilter({
          selected: data.selected,
          entity: 'Ranks',
          obj: '',
          allSelected: data.allSelected
        }); // recursive to apply the newly-(un)selected ranks
        return;
      case 'Ranks':
        cf.replaceFilter('ImageIndividuals.RankId', '');
        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.ranks);
        SidebarFilterComponent.pollSelectedOnFilterObjects(this.ranks, 'uid').subscribe(s => cf.addToFilter('ImageIndividuals.RankId', s));
        this.ranks = Object.assign([], this.ranks.sort((a, b) => {
            if(a.selected && !b.selected){
              return -1;
            } else if (!a.selected && b.selected){
              return 1;
            }

            if(a.rankName.toLocaleLowerCase() < b.rankName.toLocaleLowerCase()){
              return -1;
            } else if (a.rankName.toLocaleLowerCase() > b.rankName.toLocaleLowerCase()){
              return 1;
            } else {
              return 0;
            }
        }));
        break;
      case 'Regions':
        cf.replaceFilter('RegionalName', '');
        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.regionalNames);
        data.allSelected.subscribe(s => cf.addToFilter('RegionalName', s));
        this.regionalNames = Object.assign([], this.regionalNames.sort((a, b) => {
            if(a.selected && !b.selected){
              return -1;
            } else if (!a.selected && b.selected){
              return 1;
            }

            if(a.regionalName.toLocaleLowerCase() < b.regionalName.toLocaleLowerCase()){
              return -1;
            } else if (a.regionalName.toLocaleLowerCase() > b.regionalName.toLocaleLowerCase()){
              return 1;
            } else {
              return 0;
            }
        }));
        break;
      case 'States':
        cf.replaceFilter('LocationStateId', '');
        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.locationStates);
        data.allSelected.subscribe(s => cf.addToFilter('LocationStateId', s));
        this.locationStates = Object.assign([], this.locationStates.sort((a, b) => {
            if(a.selected && !b.selected){
              return -1;
            } else if (!a.selected && b.selected){
              return 1;
            }

            if(a.abbreviation.toLocaleLowerCase() < b.abbreviation.toLocaleLowerCase()){
              return -1;
            } else if (a.abbreviation.toLocaleLowerCase() > b.abbreviation.toLocaleLowerCase()){
              return 1;
            } else {
              return 0;
            }
        }));
        break;
      case 'Image Types':
        if (data.obj === 'All') {
          ImageType.imageTypes.forEach(t => t.selected = data.selected);
          this.batchLogs.forEach(b => b.selected = data.selected);
        } else {
          for (const i of this.batchLogs) {
            if (i.imageTypeId === data.obj.val) {
              i.selected = data.selected;
            }
          }
        }
        this.updateFilter({
          selected: data.selected, entity: 'Upload Batches', obj: '', allSelected: data.allSelected
        }); // recursive call to apply the newly-altered Upload (Batch) Log filters.
        break;
      case 'Upload Batches':
        cf.replaceFilter('BatchNo', '');
        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.batchLogs);
        SidebarFilterComponent.pollSelectedOnFilterObjects(this.batchLogs, 'uid').subscribe(s => cf.addToFilter('BatchNo', s));
        this.batchLogs = Object.assign([], this.batchLogs.sort((a, b) => {
            if(a.selected && !b.selected){
              return -1;
            } else if (!a.selected && b.selected){
              return 1;
            }

            if(a.batchName.toLocaleLowerCase() < b.batchName.toLocaleLowerCase()){
              return -1;
            } else if (a.batchName.toLocaleLowerCase() > b.batchName.toLocaleLowerCase()){
              return 1;
            } else {
              return 0;
            }
        }));
        break;
      case 'Years': // (RelevantDates/ImageDates)
        cf.replaceFilter('RelevantDates', '');
        data.allSelected.subscribe(s => cf.addToFilter('RelevantDates', +s));
        break;
      // case 'Quarters':
      //   cf.replaceFilter('RelevantDates.Quarter', '');
      //   this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.quarters);
      //   data.allSelected.subscribe(s => cf.addToFilter('RelevantDates.Quarter', s));
      //   break;
      case 'WeaponCats':
        if (data.obj === 'All') {
          this.weaponCats.forEach(w => {
            w.selected = data.selected;
            this.weapons.forEach(ww => ww.primaryWeapon.selected = false);
          });
          this.weapons.forEach(w => w.selected = data.selected);
        } else {
          for (const w of this.weapons) {
            if (w.primaryWeaponUID === data.obj.uid) {
              w.selected = data.selected;
            }
          }
        }
        this.updateFilter({
          selected: data.selected,
          entity: 'Weapons',
          obj: '',
          allSelected: data.allSelected
        }); // recursively run func to apply the newly-changed weapon filter selections
        this.weaponCats = Object.assign([], this.weaponCats.sort((a, b) => {
            if(a.selected && !b.selected){
              return -1;
            } else if (!a.selected && b.selected){
              return 1;
            }

            if(a.primaryWeaponName.toLocaleLowerCase() < b.primaryWeaponName.toLocaleLowerCase()){
              return -1;
            } else if (a.primaryWeaponName.toLocaleLowerCase() > b.primaryWeaponName.toLocaleLowerCase()){
              return 1;
            } else {
              return 0;
            }
        }));
        return;
        //break;
      case 'Weapons':
        cf.replaceFilter('Weapons', '');
        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.weapons);
        SidebarFilterComponent.pollSelectedOnFilterObjects(this.weapons, 'uid').subscribe(s => cf.addToFilter('Weapons', s));
        this.weapons = Object.assign([], this.weapons.sort((a, b) => {
            if(a.selected && !b.selected){
              return -1;
            } else if (!a.selected && b.selected){
              return 1;
            }

            if(a.weapon.toLocaleLowerCase() < b.weapon.toLocaleLowerCase()){
              return -1;
            } else if (a.weapon.toLocaleLowerCase() > b.weapon.toLocaleLowerCase()){
              return 1;
            } else {
              return 0;
            }
        }));
        break;
      case 'Portrait/Field':
        if (data.obj === 'All') {
          data.obj = '';
        }
        cf.replaceFilter('Portrait_Field', data.obj);
        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.PFRadio);
        break;
      case 'Mounted':
        if (data.obj === 'All') {
          data.obj = '';
        }
        cf.replaceFilter('Mounted', data.obj);
        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.mountedFilter);
        break;
      case 'Checked Out':
        if (data.obj === 'All') {
          data.obj = '';
        }
        cf.replaceFilter('CheckedOut', data.obj);
        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.checkedOutFilter);
        break;
      case 'Fully Tagged':
        if (data.obj === 'All'){
          data.obj = '';
        }
        cf.replaceFilter('FullyTagged',data.obj);
        this.galleryTypeSvc.setCurrentFilterOptions(data.entity, this.fullyTaggedFilter);
        break;

      default:
        console.warn('UpdateFilter function call for (' + data.entity + ') did not match any known filters.');
        cf = this.resetFilters(true);
        break;
    }
    this.applyFilters.emit(cf);
    this.cd.detectChanges();
  }

  applyIndividualSearch(individual: ImageIndividual) {
    if (individual) {
      if (this.sumOfFiltersApplied() > 0 || this.galleryTypeSvc.getCurrentLibraryFilters().searchPhrase.length > 0) {
        const modalOptions = new StarbaseModalOptions(
          'Confirm Individual Search',
          'This will clear all current Filters and searches. Are you sure you want to continue?',
          [
            new StarbaseModalButton(
              'Yes',
              () => {
                this.resetFilters(false); //Reset all filters before applying current filters
                this.searchIndividual = individual;
                this.runIndividualSearch();
                this.collapseAllFilters('individualopen');
                this.closeFilterBoxes();
                this.modalSvc.closeModal('Continue Individual Search');
                this.overlaySvc.disableOverlay();
              }
            ),
            new StarbaseModalButton(
              'No',
              () => {
                this.modalSvc.closeModal('Cancel Individual Search');
                this.overlaySvc.disableOverlay();
              }
            )
          ],
          StarbaseModalTypes.Notice
        );
        this.overlaySvc.enableOverlay();
        this.modalSvc.postModal(modalOptions);
      } else {
        this.searchIndividual = individual;
        this.runIndividualSearch();
        this.collapseAllFilters('individualopen');
        this.closeFilterBoxes();
      }
    }
  }

  runIndividualSearch() {
    if (this.searchIndividual.rank) {
      if (this.searchIndividual.rank.rankCategoryId > 0 && !(this.searchIndividual.rankId > 0)) {
        this.rankCats.forEach(rc => rc.selected = rc.uid === this.searchIndividual.rank.rankCategoryId);
        const event = {
          selected: true,
          entity: 'RankCats',
          obj: this.rankCats.find(rc => rc.uid === this.searchIndividual.rank.rankCategoryId),
          allSelected: null
        };
        this.updateFilter(event);
      } else if (this.searchIndividual.rankId > 0) {
        this.ranks.forEach(r => r.selected = r.uid === this.searchIndividual.rankId);
        const event = {
          selected: true,
          entity: 'Ranks',
          obj: this.ranks.filter(r => r.uid === this.searchIndividual.rankId)[0],
          allSelected: null
        };
        this.updateFilter(event);
      }
    }

    if (this.searchIndividual.army || this.searchIndividual.corps || this.searchIndividual.regimentNo
        || this.searchIndividual.state || this.searchIndividual.branch || this.searchIndividual.companyLetters?.length > 0) {
      const unit = {
        army: this.searchIndividual.army,
        corps: this.searchIndividual.corps,
        regiment: this.searchIndividual.regimentNo,
        state: this.searchIndividual.state,
        branch: this.searchIndividual.branch,
        coLetters: (this.searchIndividual.companyLetters.length > 0)
          ? this.searchIndividual.companyLetters.split(',') : ['']
      };

      this.activeUnitFilters = [unit];

      this.translateUnitToFilter();
    }

    const cf = this.galleryTypeSvc.getCurrentLibraryFilters();
    cf.searchFilters = [];

    if (this.searchIndividual.firstName?.length > 0) {
      cf.searchFilters.push(['FirstName', this.searchIndividual.firstName]);
    }
    if (this.searchIndividual.lastName?.length > 0) {
      cf.searchFilters.push(['LastName', this.searchIndividual.lastName]);
    }
    if (cf.searchFilters.length > 0) {
      this.applyFilters.emit(cf);
    }
  }

  clearIndividualSearch() {
    const modalOptions = new StarbaseModalOptions(
      'Clear Filters',
      'This will clear all current Filters and searches. Are you sure you want to continue?',
      [
        new StarbaseModalButton(
          'Yes',
          () => {
            this.resetFilters(true);
            this.closeFilterBoxes();
            this.modalSvc.closeModal('Continue Individual Search');
            this.overlaySvc.disableOverlay();
          }
        ),
        new StarbaseModalButton(
          'No',
          () => {
            this.modalSvc.closeModal('Cancel Individual Search');
            this.overlaySvc.disableOverlay();
          }
        )
      ],
      StarbaseModalTypes.Notice
    );

    this.overlaySvc.enableOverlay();
    this.modalSvc.postModal(modalOptions);
  }
}
